import type { ListView } from 'app/types/navigation'
import { filterListPath, manualListPath } from 'lib/lists'
import { SYSTEM_LIST_BY_QUEUE_ID, SYSTEM_PIPELINE_LIST, listPath } from 'lib/systemLists'

const CUSTOM_PAGE_ID = /^[0-9a-f]{24}$/

const WORK_ROUTE = /^\/action-center\/work\/([^/]+)\/?$/
const PIPELINES_ROUTE = /^\/pipelines\/?$/
const CONTACTS_ROUTE = /^\/contacts\/?$/
const LISTS_ROUTE = /^\/lists\/([^/?]+)\/?$/

/** Slice slugs that shipped briefly as their own lists, now bands of a family. */
const RETIRED_SLICE_SLUGS: Readonly<Record<string, { slug: string; band: string }>> = {
  'hygiene-coming-due': { slug: `hygiene-recall`, band: `hygiene-coming-due` },
  'hygiene-overdue': { slug: `hygiene-recall`, band: `due-for-hygiene` },
  'hygiene-long-past-due': { slug: `hygiene-recall`, band: `hygiene-long-past-due` },
  'treatment-fresh': { slug: `unscheduled-treatment`, band: `fresh` },
  'treatment-cooling': { slug: `unscheduled-treatment`, band: `cooling` },
  'treatment-cold': { slug: `unscheduled-treatment`, band: `cold` },
  'treatment-stale': { slug: `unscheduled-treatment`, band: `stale` },
}

const CONTACT_TAB_BAND: Readonly<Record<string, string>> = {
  'due-for-hygiene': `due-for-hygiene`,
  'due-in-30-days': `hygiene-coming-due`,
}

function withParams(path: string, params: URLSearchParams): string {
  const rest = params.toString()
  return rest ? `${path}${path.includes(`?`) ? `&` : `?`}${rest}` : path
}

function own<T>(table: Readonly<Record<string, T>>, key: string): T | undefined {
  return Object.prototype.hasOwnProperty.call(table, key) ? table[key] : undefined
}

/** The old Pipelines home itself, which names no list of its own. */
export function isBarePipelinesPath(pathname: string): boolean {
  return PIPELINES_ROUTE.test(pathname)
}

/** The `/lists/...` URL an old queue, system pipeline or preset tab URL now means, or undefined. */
export function legacyListRedirect(pathname: string, search: string): string | undefined {
  const params = new URLSearchParams(search)
  const work = pathname.match(WORK_ROUTE)
  if (work) {
    const hit = SYSTEM_LIST_BY_QUEUE_ID.get(work[1])
    return hit && withParams(listPath(hit.def.slug, `focus`, hit.bandId), params)
  }
  if (PIPELINES_ROUTE.test(pathname)) {
    const tab = params.get(`tab`) ?? ``
    const slug = own(SYSTEM_PIPELINE_LIST, tab)
    if (slug) {
      const view: ListView = params.get(`view`) === `table` ? `table` : `board`
      const band = params.get(`band`)
      params.delete(`tab`)
      params.delete(`view`)
      params.delete(`band`)
      return withParams(listPath(slug, view, band ?? undefined), params)
    }
    if (!tab) return undefined
    params.delete(`tab`)
    return withParams(manualListPath(tab), params)
  }
  if (CONTACTS_ROUTE.test(pathname)) {
    const tab = params.get(`tab`)
    params.delete(`tab`)
    if (tab === null || tab === ``) return withParams(listPath(`active-leads`, `table`), params)
    const band = own(CONTACT_TAB_BAND, tab)
    if (band) return withParams(listPath(`hygiene-recall`, `table`, band), params)
    return CUSTOM_PAGE_ID.test(tab) ? withParams(filterListPath(tab), params) : undefined
  }
  const listsMatch = pathname.match(LISTS_ROUTE)
  if (listsMatch) {
    const retired = own(RETIRED_SLICE_SLUGS, listsMatch[1])
    if (!retired) return undefined
    const urlView = params.get(`view`)
    const view: ListView | undefined =
      urlView === `focus` || urlView === `board` || urlView === `table` ? urlView : undefined
    params.delete(`view`)
    params.delete(`band`)
    return withParams(listPath(retired.slug, view, retired.band), params)
  }
  return undefined
}
